import { useState } from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'
import type { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { formatDate, toIsoDateString } from '@/lib/format'

const PRESETS = [
  { value: 'all', labelKey: 'reviews.filters.allTime' },
  { value: 'today', labelKey: 'reviews.filters.today' },
  { value: 'week', labelKey: 'reviews.filters.thisWeek' },
  { value: 'month', labelKey: 'reviews.filters.thisMonth' },
  { value: 'quarter', labelKey: 'reviews.filters.last3Months' },
] as const

export type DatePreset = (typeof PRESETS)[number]['value']

/**
 * The date filter is either one of the server's presets or an explicit range.
 *
 * `preset` is a literal union rather than `string` so TypeScript can discriminate the
 * two arms — with `string` the first arm also matches `'custom'` and `range` stays
 * possibly-undefined at every use site.
 */
export type DateFilterValue =
  | { preset: DatePreset; range?: undefined }
  | { preset: 'custom'; range: DateRange }

interface DateRangeFilterProps {
  value: DateFilterValue
  onChange: (value: DateFilterValue) => void
}

/**
 * Presets and an arbitrary range behind one control.
 *
 * This replaces the preset dropdown rather than sitting beside it. Two separate date
 * controls raise the question of which one wins when both are set — the pattern Stripe,
 * Linear and GA all settled on puts the shortcuts in a rail next to the calendar, so a
 * preset stays one click and a custom range needs no second control.
 */
export function DateRangeFilter({ value, onChange }: DateRangeFilterProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  // The calendar edits a draft, not the live filter. react-day-picker returns a
  // *complete* range on the very first click ({from: day, to: day}), so committing on
  // every selection meant one click silently ran a one-day search and shut the panel
  // before you could pick the end date.
  const [draft, setDraft] = useState<DateRange | undefined>(
    value.preset === 'custom' ? value.range : undefined
  )

  const label = (() => {
    if (value.preset === 'custom') {
      const { from, to } = value.range
      if (from && to) return `${formatDate(from)} – ${formatDate(to)}`
      if (from) return t('reviews.filters.fromDate', { date: formatDate(from) })
      return t('reviews.filters.customRange')
    }
    const preset = PRESETS.find((p) => p.value === value.preset)
    return t(preset?.labelKey ?? 'reviews.filters.allTime')
  })()

  /** Discard an abandoned half-selection: reopening starts from what is applied. */
  function handleOpenChange(next: boolean) {
    if (next) setDraft(value.preset === 'custom' ? value.range : undefined)
    setOpen(next)
  }

  // A preset is one unambiguous click, so it still applies immediately. Only the
  // calendar needs a commit step, because it takes two clicks to say one thing.
  function selectPreset(preset: DatePreset) {
    onChange({ preset })
    setOpen(false)
  }

  function applyDraft() {
    if (!draft?.from) return
    onChange({ preset: 'custom', range: draft })
    setOpen(false)
  }

  const draftFrom = draft?.from
  const draftTo = draft?.to
  // One click gives from === to. Until they differ the user has picked a start and is
  // still choosing an end, so the footer prompts rather than claiming a range.
  const draftIsComplete = !!(
    draftFrom &&
    draftTo &&
    toIsoDateString(draftFrom) !== toIsoDateString(draftTo)
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'text-sm min-w-[130px] justify-start font-normal',
            value.preset === 'all' && 'text-muted-foreground'
          )}
        >
          <CalendarIcon className="h-3.5 w-3.5 mr-2 shrink-0 text-muted-foreground" />
          <span className="truncate">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col sm:flex-row">
          <div className="flex sm:flex-col gap-1 p-2 sm:border-r border-b sm:border-b-0 overflow-x-auto">
            {PRESETS.map((preset) => (
              <Button
                key={preset.value}
                variant={value.preset === preset.value ? 'secondary' : 'ghost'}
                size="sm"
                className="justify-start text-xs whitespace-nowrap"
                onClick={() => selectPreset(preset.value)}
              >
                {t(preset.labelKey)}
              </Button>
            ))}
          </div>
          <Calendar
            mode="range"
            numberOfMonths={2}
            defaultMonth={draftFrom ?? (value.preset === 'custom' ? value.range.from : undefined)}
            selected={draft}
            onSelect={setDraft}
            disabled={{ after: new Date() }}
            autoFocus
          />
        </div>

        <div className="flex items-center justify-between gap-3 border-t p-2 pl-3">
          <span className="text-xs text-muted-foreground truncate">
            {draftIsComplete
              ? `${formatDate(draftFrom!)} – ${formatDate(draftTo!)}`
              : draftFrom
                ? t('reviews.filters.pickEndDate')
                : t('reviews.filters.pickStartDate')}
          </span>
          <Button size="sm" onClick={applyDraft} disabled={!draftFrom}>
            {t('reviews.filters.apply')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
